/**
 * OBK-7 (K44) — durable "Needs Confirmation" queue, pure helpers.
 *
 * OptiQueue bookings that require shop follow-up are persisted on
 * `tickets.confirmation_status` (`needs_contact | contacted | confirmed`,
 * indexed by `idx_tickets_confirmation_status (shop_id, confirmation_status)`).
 * The 30-second toast (`useConfirmationAlerts`) is an immediate alert only and
 * can disappear; this module backs a durable queue VIEW driven by that
 * persisted column, so an operator alert can no longer be silently dropped.
 *
 * Pure + unit-tested. No React, no Supabase — the hook/component compose these.
 */

export type ConfirmationStatus = 'needs_contact' | 'contacted' | 'confirmed';

/**
 * Statuses that keep a booking in the ACTIVE queue (still needs operator
 * action). `confirmed` (and `null`/standard bookings) are NOT in the queue.
 */
export const NEEDS_CONFIRMATION_ACTIVE_STATUSES: readonly ConfirmationStatus[] = [
  'needs_contact',
  'contacted',
];

/** True when a ticket's confirmation_status is still unresolved (in-queue). */
export function isUnresolvedNeedsConfirmation(
  status: string | null | undefined,
): status is ConfirmationStatus {
  return (
    status === 'needs_contact' || status === 'contacted'
  );
}

export interface NeedsConfirmationTicket {
  id: string;
  confirmation_status?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  customer_request_notes?: string | null;
  intent?: string | null;
  start_time?: string | null;
}

/** Keep only unresolved (active) needs-confirmation rows. */
export function filterActiveNeedsConfirmation<T extends { confirmation_status?: string | null }>(
  rows: readonly T[] | null | undefined,
): T[] {
  if (!rows) return [];
  return rows.filter((r) => isUnresolvedNeedsConfirmation(r.confirmation_status));
}

/**
 * Roles allowed to SEE and RESOLVE the queue. Mirrors usePermissions()
 * `canEditTickets` (OWNER / MANAGER / SERVICE_WRITER / ORG_ADMIN) so customer
 * contact PII (name/phone/notes) is never surfaced to unauthorized roles
 * (e.g. TECHNICIAN) via this queue.
 */
export const NEEDS_CONFIRMATION_ROLES: readonly string[] = [
  'OWNER',
  'MANAGER',
  'SERVICE_WRITER',
  'ORG_ADMIN',
];

export function canViewNeedsConfirmationQueue(role: string | null | undefined): boolean {
  return !!role && NEEDS_CONFIRMATION_ROLES.includes(role);
}

export type ResolveAction = 'mark_contacted' | 'confirm';

/** The persisted status a resolve action transitions a ticket to. */
export function nextConfirmationStatus(action: ResolveAction): ConfirmationStatus {
  return action === 'mark_contacted' ? 'contacted' : 'confirmed';
}

/** Human label for the booking lane/intent. */
export function needsConfirmationLaneLabel(intent: string | null | undefined): string {
  return intent === 'callback_requested' ? 'Callback Requested' : 'Requested Work';
}

/** In-app route that opens a ticket's details (Dashboard highlight param). */
export function needsConfirmationOpenRoute(ticketId: string): string {
  return `/?highlight=${encodeURIComponent(ticketId)}&openDetails=true`;
}
